import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiChevronLeft, FiChevronRight, FiUsers } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import { FaQrcode } from 'react-icons/fa';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Design tokens (matching CustomerDetail.js)
const colors = {
  stone50: '#fafaf9',
  stone100: '#f5f5f4',
  stone200: '#e7e5e4',
  stone300: '#d6d3d1',
  stone400: '#a8a29e',
  stone500: '#78716c',
  stone600: '#57534e',
  stone700: '#44403c',
  stone800: '#292524',
  stone900: '#1c1917',
  accent50: '#ecfeff',
  accent100: '#cffafe',
  accent500: '#06b6d4',
  accent600: '#0891b2',
  accent700: '#0e7490',
};

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`,
    padding: '24px 32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  iconBox: {
    width: 48,
    height: 48,
    borderRadius: 14,
    background: `linear-gradient(135deg, ${colors.accent500} 0%, ${colors.accent700} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.stone800,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 13,
    color: colors.stone500,
    margin: '2px 0 0',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${colors.stone200}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
    marginBottom: 20,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '14px 20px',
    borderBottom: `1px solid ${colors.stone100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    maxWidth: 360,
    minWidth: 200,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.stone400,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px 8px 36px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone800,
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: 12,
  },
  th: {
    textAlign: 'left',
    padding: '8px 14px',
    fontSize: 10,
    fontWeight: 600,
    color: colors.stone500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: colors.stone50,
    borderBottom: `1px solid ${colors.stone200}`,
  },
  td: {
    padding: '8px 14px',
    borderBottom: `1px solid ${colors.stone100}`,
    color: colors.stone700,
  },
  btnSecondary: {
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 500,
    color: colors.stone700,
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  pageBtn: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 6,
    color: colors.stone600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  select: {
    padding: '8px 28px 8px 12px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone700,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    padding: '12px 18px',
    background: colors.stone800,
    color: '#fff',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 500,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    animation: 'slideUp 0.3s ease',
  },
  toastError: {
    background: '#be123c',
  },
  customerName: {
    fontWeight: 600,
    color: colors.stone800,
  },
  emailText: {
    color: colors.accent600,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
    padding: 16,
  },
  modal: {
    width: '100%',
    maxWidth: 520,
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    borderRadius: 18,
    border: `1px solid ${colors.stone200}`,
    boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.stone100}`,
  },
  modalTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: colors.stone800,
    letterSpacing: '-0.01em',
  },
  modalBody: {
    padding: 16,
  },
  modalFooter: {
    padding: 16,
    borderTop: `1px solid ${colors.stone100}`,
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    background: `linear-gradient(135deg, ${colors.accent600} 0%, ${colors.accent700} 100%)`,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 6px 18px rgba(8,145,178,0.25)',
  },
  qrCard: {
    border: `1px solid ${colors.stone200}`,
    background: '#fff',
    borderRadius: 14,
    padding: 14,
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  qrMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  qrMetaTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: colors.stone800,
    margin: 0,
  },
  qrMetaSub: {
    fontSize: 12,
    color: colors.stone500,
    margin: 0,
  },
  deptList: {
    display: 'grid',
    gap: 8,
    marginTop: 12,
  },
  deptBtn: {
    padding: '10px 12px',
    borderRadius: 10,
    border: `1.5px solid ${colors.stone200}`,
    background: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  tinyLink: {
    fontSize: 12,
    color: colors.stone600,
    marginTop: 10,
    wordBreak: 'break-all',
  }
};

export default function Customers() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState({ message: '', isError: false });
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [requestQrModalOpen, setRequestQrModalOpen] = useState(false);
  const [selectedDepartmentForQR, setSelectedDepartmentForQR] = useState(null);

  const fetchCustomers = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/companies/${company.id}/customers`, { params: { page, limit: pageSize } });
      const list = data?.customers || [];
      setCustomers(list);
      setTotalPages(data?.totalPages || 1);
      setTotalCustomers(data?.totalCustomers || list.length || 0);
    } catch (e) {
      console.error('Failed to load customers', e);
      setToast({ message: 'Failed to load customers', isError: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, [company?.id, page, pageSize]);

  const fetchDepartments = async () => {
    if (!company?.id) return;
    setDepartmentsLoading(true);
    try {
      const { data } = await api.get(`/api/departments/${company.id}`);
      const list = data?.departments || [];
      setDepartments(list);
      return list;
    } catch (e) {
      console.error('Failed to load departments', e);
      setToast({ message: 'Failed to load teams', isError: true });
      return [];
    } finally {
      setDepartmentsLoading(false);
    }
  };

  useEffect(() => {
    if (!toast.message) return;
    const t = setTimeout(() => setToast({ message: '', isError: false }), 3000);
    return () => clearTimeout(t);
  }, [toast.message]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q)
    );
  }, [customers, search]);

  const handleOpen = (customer) => {
    navigate(`/customers/${customer.id}`, { state: { customer } });
  };

  const getRequestFormUrl = useMemo(() => {
    if (!company?.id || !selectedDepartmentForQR?.id) return '';
    const base = `${window.location.origin}/company/${company.id}/customer`;
    return `${base}?departmentId=${selectedDepartmentForQR.id}`;
  }, [company?.id, selectedDepartmentForQR?.id]);

  const handleOpenRequestQr = async () => {
    setRequestQrModalOpen(true);
    const list = await fetchDepartments();
    setSelectedDepartmentForQR((prev) => {
      if (prev?.id && list.some((d) => String(d.id) === String(prev.id))) return prev;
      return list[0] || null;
    });
  };

  const handleCopyQrLink = async () => {
    const url = getRequestFormUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setToast({ message: 'Link copied', isError: false });
    } catch (e) {
      console.error('Failed to copy link', e);
      setToast({ message: 'Failed to copy link', isError: true });
    }
  };

  const handlePrintQrCard = () => {
    if (!getRequestFormUrl) return;
    try { window.print(); } catch {}
  };

  const renderEmptyState = () => (
    <tr>
      <td colSpan={4} style={{ padding: '40px 16px', textAlign: 'center' }}>
        <div style={{ color: colors.stone400, fontSize: 12 }}>No customers found</div>
      </td>
    </tr>
  );

  const renderLoading = () => (
    <tr>
      <td colSpan={4} style={{ padding: '40px 16px', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: 20, height: 20, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </td>
    </tr>
  );

  return (
    <div className="page-content">
      <style>{`
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Print only the QR business card */
        @media print {
          body * { visibility: hidden !important; }
          #bn-print-area, #bn-print-area * { visibility: visible !important; }
          #bn-print-area { position: fixed !important; inset: 0 !important; display: grid !important; place-items: center !important; background: #fff !important; }
        }

        /* Make the QR modal usable on mobile */
        @media (max-width: 720px) {
          .bn-qr-split { grid-template-columns: 1fr !important; }
          .bn-qr-lhs { border-right: none !important; border-bottom: 1px solid #f5f5f4 !important; }
        }
      `}</style>

      {/* Toast */}
      {!!toast.message && (
        <div style={{ ...styles.toast, ...(toast.isError ? styles.toastError : {}) }}>
          <span>{toast.message}</span>
          <button onClick={() => setToast({ message: '', isError: false })} style={{ padding: 0, background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex' }}>
            <FiX size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
       {/*  <div style={styles.iconBox}>
          <FiUsers size={22} />
        </div> */}
        <div>
          <h1 style={styles.title}>Customers</h1>
          <p style={styles.subtitle}>{totalCustomers} total customers</p>
        </div>
      </div>

      {/* Main Card */}
      <div style={styles.card}>
        {/* Search Header */}
        <div style={styles.cardHeader}>
          <div style={styles.searchWrapper}>
            <FiSearch size={15} style={styles.searchIcon} />
            <input
              style={styles.searchInput}
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {search && (
              <button
                style={styles.btnSecondary}
                onClick={() => { setSearch(''); setPage(1); }}
              >
                Clear
              </button>
            )}
            <span
              role="button"
              tabIndex={0}
              title="Request intake QR cards"
              aria-label="Request intake QR cards"
              onClick={handleOpenRequestQr}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenRequestQr(); } }}
              style={{ ...styles.pageBtn, width: 34, height: 34, color: colors.stone700 }}
            >
              <FaQrcode style={{ fontSize: 16 }} />
            </span>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && renderLoading()}
              {!loading && filtered.length === 0 && renderEmptyState()}
              {!loading && filtered.map(c => (
                <tr 
                  key={c.id} 
                  style={{ transition: 'background 0.15s ease', cursor: 'pointer' }} 
                  onMouseEnter={e => e.currentTarget.style.background = colors.stone50} 
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => handleOpen(c)}
                >
                  <td style={styles.td}>
                    <span style={styles.customerName}>{c.name || '-'}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.emailText}>{c.email || '-'}</span>
                  </td>
                  <td style={styles.td}>{c.phone || '-'}</td>
                  <td style={styles.td}>{(c.createdAt || '').toString().slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${colors.stone100}` }}>
          <span style={{ fontSize: 12, color: colors.stone500 }}>
            Showing <strong style={{ color: colors.stone700 }}>{Math.min((page - 1) * pageSize + 1, totalCustomers)}-{Math.min(page * pageSize, totalCustomers)}</strong> of <strong style={{ color: colors.stone700 }}>{totalCustomers}</strong>
          </span>
          <div style={styles.pagination}>
            <button
              style={{ ...styles.pageBtn, opacity: page <= 1 ? 0.5 : 1 }}
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              <FiChevronLeft size={16} />
            </button>
            <span style={{ padding: '0 12px', fontSize: 12, color: colors.stone600 }}>Page {page}</span>
            <button
              style={{ ...styles.pageBtn, opacity: page >= totalPages ? 0.5 : 1 }}
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              <FiChevronRight size={16} />
            </button>
            <select
              style={{ ...styles.select, marginLeft: 8 }}
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Request QR card modal (teams on LHS, QR preview on RHS) */}
      {requestQrModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setRequestQrModalOpen(false)} role="dialog" aria-modal="true">
          <div style={{ ...styles.modal, maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <h3 style={styles.modalTitle}>Request intake QR cards</h3>
                <div style={{ fontSize: 12, color: colors.stone500 }}>
                  Select a team on the left to generate a QR card.
                </div>
              </div>
              <button
                onClick={() => setRequestQrModalOpen(false)}
                style={{ padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', color: colors.stone600, display: 'flex' }}
                aria-label="Close"
                title="Close"
              >
                <FiX size={18} />
              </button>
            </div>

            <div style={{ ...styles.modalBody, padding: 0 }}>
              <div className="bn-qr-split" style={{ display: 'grid', gridTemplateColumns: '320px 1fr' }}>
                {/* Teams list */}
                <div className="bn-qr-lhs" style={{ borderRight: `1px solid ${colors.stone100}`, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors.stone700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Teams
                    </div>
                    {/* <button style={styles.btnSecondary} onClick={() => fetchDepartments()}>
                      Refresh
                    </button> */}
                  </div>

                  {departmentsLoading && (
                    <div style={{ fontSize: 12, color: colors.stone500 }}>Loading teams…</div>
                  )}
                  {!departmentsLoading && (departments || []).length === 0 && (
                    <div style={{ fontSize: 12, color: colors.stone500 }}>No teams found.</div>
                  )}

                  <div style={{ display: 'grid', gap: 8 }}>
                    {!departmentsLoading && (departments || []).map((d) => {
                      const active = String(d.id) === String(selectedDepartmentForQR?.id);
                      return (
                        <button
                          key={d.id}
                          style={{
                            ...styles.deptBtn,
                            borderColor: active ? colors.accent600 : colors.stone200,
                            background: active ? colors.accent50 : '#fff',
                          }}
                          onClick={() => setSelectedDepartmentForQR(d)}
                        >
                          <span style={{ fontSize: 13, fontWeight: 700, color: colors.stone800, textAlign: 'left' }}>
                            {d.name || `Team ${d.id}`}
                          </span>
                          <span style={{ fontSize: 12, color: active ? colors.accent700 : colors.stone500 }}>
                            {active ? 'Selected' : 'View'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* QR preview */}
                <div style={{ padding: 16 }}>
                  {!selectedDepartmentForQR ? (
                    <div style={{ fontSize: 12, color: colors.stone500 }}>Select a team to preview the QR card.</div>
                  ) : (
                    <>
                      <div style={styles.qrCard}>
                        <div style={styles.qrMeta}>
                          <p style={styles.qrMetaTitle}>{selectedDepartmentForQR.name || 'Team'}</p>
                          <p style={styles.qrMetaSub}>Scan to get in touch</p>
                        </div>
                        <div style={{ background: '#fff', padding: 10, borderRadius: 12, border: `1px solid ${colors.stone200}` }}>
                          <QRCodeSVG
                            value={getRequestFormUrl}
                            size={160}
                            level="M"
                            includeMargin={false}
                            imageSettings={{
                              src: company?.logo || '/logo.png',
                              height: 30,
                              width: 30,
                              excavate: true
                            }}
                          />
                        </div>
                      </div>
                      <div style={styles.tinyLink}>{getRequestFormUrl}</div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.btnSecondary} onClick={() => setRequestQrModalOpen(false)}>Close</button>
              <button style={styles.btnSecondary} onClick={handleCopyQrLink} disabled={!getRequestFormUrl}>Copy link</button>
              <button
                style={styles.btnSecondary}
                onClick={() => { try { window.open(getRequestFormUrl, '_blank', 'noopener,noreferrer'); } catch {} }}
                disabled={!getRequestFormUrl}
              >
                Open form
              </button>
              <button className="btn-primary" onClick={handlePrintQrCard} disabled={!getRequestFormUrl}>Print card</button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden print area (business card style) */}
      {requestQrModalOpen && selectedDepartmentForQR && !!getRequestFormUrl && (
        <div id="bn-print-area" style={{ position: 'fixed', left: -9999, top: 0 }}>
          <div style={{
            width: '3.5in',
            height: '2in',
            borderRadius: 18,
            border: '1px solid #e5e7eb',
            padding: 14,
            display: 'flex',
            gap: 14,
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#fff',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', lineHeight: '18px' }}>
                {company?.name || 'BloomNav'}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                {selectedDepartmentForQR.name || 'Team'}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Scan to submit a request</div>
              <div style={{ fontSize: 9, color: '#9ca3af', wordBreak: 'break-all', lineHeight: '12px' }}>
                {getRequestFormUrl}
              </div>
            </div>
            <div style={{ padding: 6, border: '1px solid #e5e7eb', borderRadius: 14 }}>
              <QRCodeSVG
                value={getRequestFormUrl}
                size={120}
                level="M"
                includeMargin={false}
                imageSettings={{
                  src: company?.logo || '/logo.png',
                  height: 24,
                  width: 24,
                  excavate: true
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
